import { ArrowRight, Map, Navigation, Phone, StickyNote } from 'lucide-react';
import { useEffect, useState } from 'react';
import { money, useConfirm } from '@despachador/ui';
import { useDriver } from '../store';
import { ReasonModal, RoutePoints } from '../components';
import type { ActiveService } from '../types';
import { RadioTalk } from './Radio';

/** The four moments of a trip for the driver; each one has exactly one way forward. */
const STEPS = ['ACCEPTED', 'DRIVER_EN_ROUTE', 'DRIVER_ARRIVED', 'IN_PROGRESS'] as const;
const STEP_LABEL = ['Aceptado', 'En camino', 'En el punto', 'En viaje'];
const NEXT: Record<string, { action: string; label: string; tone: string; title: string } | undefined> = {
  ACCEPTED: { action: 'en_route', label: 'Voy en camino', tone: 'primary', title: 'Sal a recoger al cliente' },
  DRIVER_EN_ROUTE: { action: 'arrived', label: 'Llegué al punto', tone: 'primary', title: 'Vas camino al cliente' },
  DRIVER_ARRIVED: { action: 'start', label: 'Iniciar viaje', tone: 'ok', title: 'Espera al cliente en el punto' },
  IN_PROGRESS: { action: 'complete', label: 'Finalizar servicio', tone: 'ok', title: 'Lleva al cliente a su destino' },
};

const nav = (lat: number, lng: number) => ({
  google: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
  waze: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
});

/**
 * The trip in progress. What the driver needs now is on top (which step, where to go, how to get
 * there, who the customer is); the next step is fixed at the bottom, under the thumb, with the radio
 * beside it; what ends the trip early is apart and quiet (links), so it is never hit by accident.
 */
export function Trip({ service }: { service: ActiveService }) {
  const { action } = useDriver();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<null | 'cancel' | 'no_show'>(null);
  // each step puts a new button exactly where the last one was: a double tap must not skip a step
  const [settling, setSettling] = useState(false);
  useEffect(() => {
    setSettling(true);
    const id = setTimeout(() => setSettling(false), 900);
    return () => clearTimeout(id);
  }, [service.status]);
  const next = NEXT[service.status];
  const step = STEPS.indexOf(service.status as (typeof STEPS)[number]);
  const toPickup = service.status !== 'IN_PROGRESS';
  const target = toPickup ? { lat: service.origin_lat, lng: service.origin_lng } : { lat: service.destination_lat, lng: service.destination_lng };
  // a destination known only as text has no point: the customer gives directions
  const links = target.lat == null || target.lng == null ? null : nav(target.lat, target.lng);
  const fare = money(service.fare_total_minor, service.currency);
  const run = async (a: string, reason?: string) => {
    setBusy(true);
    const ok = await action(a, reason);
    setBusy(false);
    // keep the reason dialog open when it failed, so nothing has to be chosen again
    if (ok) setModal(null);
  };
  const advance = async () => {
    if (!next) return;
    // closing the trip is irreversible and the button is big: one explicit confirmation, with the fare
    if (
      next.action === 'complete' &&
      !(await confirm({
        title: '¿Finalizar el servicio?',
        message: (
          <>
            Confirma que el cliente llegó a su destino. No se puede deshacer.
            <span className="confirm-fare">
              <span>
                Tarifa <span className="code">{service.code}</span>
              </span>
              <b>{fare}</b>
            </span>
          </>
        ),
        confirmLabel: 'Sí, finalizar',
        cancelLabel: 'Todavía no',
      }))
    )
      return;
    await run(next.action);
  };

  return (
    <div className="trip">
      <header className="trip-head">
        <div className="trip-head-top">
          <span className="micro-title">
            Paso {step + 1} de {STEPS.length} · {STEP_LABEL[step] ?? ''}
          </span>
          <span className="trip-head-meta">
            <span className="code">{service.code}</span>
            <b className="trip-fare">{fare}</b>
          </span>
        </div>
        <h1 className="trip-title">{next?.title ?? 'Servicio en curso'}</h1>
        <ol className="trip-rail" aria-label={`Paso ${step + 1} de ${STEPS.length}: ${STEP_LABEL[step] ?? ''}`}>
          {STEP_LABEL.map((l, i) => (
            <li key={l} className={i < step ? 'done' : i === step ? 'now' : undefined} aria-current={i === step ? 'step' : undefined}>
              <span className="sr-only">{l}</span>
            </li>
          ))}
        </ol>
      </header>

      <div className="trip-body">
        <section className="trip-route" aria-label="Recorrido">
          <RoutePoints focus={toPickup ? 'origin' : 'destination'} origin={service.origin_text ?? 'Punto en el mapa'} destination={service.destination_text ?? 'Lo indica el cliente'} />
          {links ? (
            <div className="trip-nav" role="group" aria-label={toPickup ? 'Navegar al punto de recogida' : 'Navegar al destino'}>
              <a className="btn" href={links.google} target="_blank" rel="noopener noreferrer">
                <Map size={20} aria-hidden /> Google Maps<span className="sr-only"> (abre otra aplicación)</span>
              </a>
              <a className="btn" href={links.waze} target="_blank" rel="noopener noreferrer">
                <Navigation size={20} aria-hidden /> Waze<span className="sr-only"> (abre otra aplicación)</span>
              </a>
            </div>
          ) : (
            <p className="trip-hint">El destino no está ubicado en el mapa: pide indicaciones al cliente.</p>
          )}
        </section>

        <section className="trip-customer" aria-label="Cliente">
          <div className="trip-customer-who">
            <span className="micro-title">Cliente</span>
            <b>{service.customer_name ?? 'Sin nombre'}</b>
          </div>
          {service.customer_phone ? (
            <a className="btn" href={`tel:${service.customer_phone}`}>
              <Phone size={18} aria-hidden /> Llamar
            </a>
          ) : (
            <span className="trip-hint">Sin teléfono: comunícate por la central.</span>
          )}
        </section>
        {service.notes && (
          <p className="trip-notes">
            <StickyNote size={16} aria-hidden /> {service.notes}
          </p>
        )}

        {toPickup && (
          <section className="trip-problems" aria-label="Si hay un problema">
            <h2>¿Hay un problema?</h2>
            {service.status === 'DRIVER_ARRIVED' && (
              <button type="button" className="link-btn" disabled={busy} onClick={() => setModal('no_show')}>
                El cliente no llegó
              </button>
            )}
            <button type="button" className="link-btn danger" disabled={busy} onClick={() => setModal('cancel')}>
              Cancelar servicio
            </button>
          </section>
        )}
      </div>

      {next && (
        <div className="action-bar">
          <RadioTalk />
          <button type="button" className={`btn ${next.tone} next-step`} disabled={busy || settling} onClick={() => void advance()}>
            <span>{busy ? 'Enviando…' : next.label}</span>
            <ArrowRight size={22} aria-hidden />
          </button>
        </div>
      )}

      {modal === 'cancel' && (
        <ReasonModal
          title="¿Por qué cancelas?"
          confirmLabel="Cancelar servicio"
          busy={busy}
          options={['Problema mecánico', 'Accidente o emergencia', 'Tráfico excesivo', 'Dirección incorrecta']}
          onClose={() => setModal(null)}
          onSubmit={(r) => void run('cancel', r)}
        />
      )}
      {modal === 'no_show' && (
        <ReasonModal
          title="El cliente no se presentó"
          confirmLabel="Reportar"
          busy={busy}
          options={['Esperé el tiempo reglamentario', 'El cliente no responde']}
          onClose={() => setModal(null)}
          onSubmit={(r) => void run('no_show', r)}
        />
      )}
    </div>
  );
}
